import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Container } from './styled/Container';
import { logout } from '../store/actions/authActions';
import { useLocale } from '../context/LocaleContext';
import LanguageSwitcher from './LanguageSwitcher';
import { useT } from '../hooks/useT';

const centerNavLinks = [
  { key: 'nav.categories', href: '/categories' },
  { key: 'nav.topSites', href: '/top-sites' },
  { key: 'nav.compare', href: '/compare' },
  { key: 'nav.submitSite', href: '/submit' },
];
const rightNavLinks = [{ key: 'nav.logIn', href: '/login' }];
const allNavLinks = [...centerNavLinks, ...rightNavLinks];

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${({ theme }) => theme.colors.primary};
`;

const NavInner = styled(Container)`
  display: flex;
  height: 4rem;
  align-items: center;
  justify-content: space-between;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-shrink: 0;
  min-width: 0;
`;

const LogoBox = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  display: flex;
  align-items: center;
  justify-content: center;
  & span {
    color: ${({ theme }) => theme.colors.goldForeground};
    font-weight: 800;
    font-size: 1rem;
  }
`;

const LogoText = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primaryForeground};
  letter-spacing: -0.025em;
`;

const DesktopNav = styled.div`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
    min-width: 0;
  }
`;

const DesktopNavCenter = styled.div`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    flex-wrap: nowrap;
    gap: 0.375rem;
    min-width: 0;
  }
`;

const DesktopNavRight = styled.div`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
  }
`;

const NavLink = styled(Link)`
  white-space: nowrap;
  flex-shrink: 0;
  padding: 0.5rem clamp(0.35rem, 1.1vw, 1rem);
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  line-height: 1.2;
  transition: all 0.2s;
  background: ${({ $active, theme }) => ($active ? 'rgba(255,255,255,0.2)' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primaryForeground : 'rgba(255,255,255,0.7)')};
  &:hover {
    color: ${({ theme }) => theme.colors.primaryForeground};
    background: ${({ $active }) => ($active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)')};
  }
`;

const SearchBtn = styled.button`
  margin-left: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primaryForeground};
  transition: all 0.2s;
  &:hover {
    background: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.goldForeground};
  }
`;

const NavActionBtn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  transition: all 0.2s;
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.goldForeground};
  &:hover {
    opacity: 0.9;
  }
`;

const MobileToggle = styled.button`
  @media (min-width: 1024px) {
    display: none;
  }
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primaryForeground};
`;

const SearchPanel = styled(motion.div)`
  overflow: hidden;
  background: ${({ theme }) => theme.colors.primary};
`;

const SearchForm = styled.form`
  padding-bottom: 1rem;
`;

const SearchInputWrap = styled.div`
  position: relative;
`;

const SearchIconAbs = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 3rem;
  padding-left: 3rem;
  padding-right: 7rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primaryForeground};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  outline: none;
  border: 2px solid transparent;
  transition: all 0.2s;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.gold};
  }
`;

const SearchSubmitBtn = styled.button`
  position: absolute;
  right: 0.375rem;
  top: 0.375rem;
  bottom: 0.375rem;
  padding: 0 1.5rem;
  border-radius: ${({ theme }) => theme.radius};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const MobilePanel = styled(motion.div)`
  overflow: hidden;
  @media (min-width: 1024px) {
    display: none;
  }
  background: ${({ theme }) => theme.colors.primary};
`;

const MobileMenuInner = styled.div`
  padding-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MobileNavLink = styled(Link)`
  white-space: nowrap;
  padding: 0.75rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  transition: all 0.2s;
  background: ${({ $active }) => ($active ? 'rgba(255,255,255,0.2)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : 'rgba(255,255,255,0.7)')};
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const MobileSearchForm = styled.form`
  position: relative;
  margin-top: 0.5rem;
`;

const MobileSearchInput = styled.input`
  width: 100%;
  height: 3rem;
  padding-left: 2.75rem;
  padding-right: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primaryForeground};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  outline: none;
`;

function Navbar() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { prependPath } = useLocale();
  const t = useT();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(prependPath(`/search?q=${encodeURIComponent(query.trim())}`));
      setSearchOpen(false);
      setMobileOpen(false);
      setQuery('');
    }
  };

  const isActive = (href) => {
    const path = prependPath(href);
    return location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate(prependPath('/'));
  };

  return (
    <Nav>
      <NavInner>
        <LogoLink to={prependPath('/')}>
          <LogoBox>
            <span>C</span>
          </LogoBox>
          <LogoText>CiteMatch</LogoText>
        </LogoLink>

        <DesktopNav>
          <DesktopNavCenter>
            {centerNavLinks.map((link) => (
              <NavLink key={link.href} to={prependPath(link.href)} $active={isActive(link.href)}>
                {t(link.key)}
              </NavLink>
            ))}
          </DesktopNavCenter>
          <DesktopNavRight>
            <LanguageSwitcher />
            <SearchBtn type="button" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search">
              <Search size={18} strokeWidth={2.5} />
            </SearchBtn>
            {isAuthenticated ? (
              <NavActionBtn type="button" onClick={handleLogout}>
                Log out{user?.name ? `, ${user.name}` : ''}
              </NavActionBtn>
            ) : (
              rightNavLinks.map((link) => (
                <NavLink key={link.href} to={prependPath(link.href)} $active={isActive(link.href)}>
                  {t(link.key)}
                </NavLink>
              ))
            )}
          </DesktopNavRight>
        </DesktopNav>

        <MobileToggle type="button" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
          {mobileOpen ? <X size={18} strokeWidth={2.5} /> : <Menu size={18} strokeWidth={2.5} />}
        </MobileToggle>
      </NavInner>

      <AnimatePresence>
        {searchOpen && (
          <SearchPanel
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}
          >
            <SearchForm onSubmit={handleSearch}>
              <Container>
                <SearchInputWrap>
                  <SearchIconAbs>
                    <Search size={18} />
                  </SearchIconAbs>
                  <SearchInput
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Enter a domain (e.g. stripe.com)"
                    autoFocus
                  />
                  <SearchSubmitBtn type="submit">{t('common.search')}</SearchSubmitBtn>
                </SearchInputWrap>
              </Container>
            </SearchForm>
          </SearchPanel>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileOpen && (
          <MobilePanel
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}
          >
            <Container>
              <MobileMenuInner>
                <LanguageSwitcher />
                {allNavLinks.map((link) => (
                  <MobileNavLink
                    key={link.href}
                    to={prependPath(link.href)}
                    $active={isActive(link.href)}
                    onClick={() => setMobileOpen(false)}
                  >
                    {t(link.key)}
                  </MobileNavLink>
                ))}
                {isAuthenticated ? (
                  <NavActionBtn type="button" onClick={handleLogout}>
                    Log out{user?.name ? `, ${user.name}` : ''}
                  </NavActionBtn>
                ) : null}
                <MobileSearchForm onSubmit={handleSearch}>
                  <SearchIconAbs>
                    <Search size={16} />
                  </SearchIconAbs>
                  <MobileSearchInput
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search domain..."
                  />
                </MobileSearchForm>
              </MobileMenuInner>
            </Container>
          </MobilePanel>
        )}
      </AnimatePresence>
    </Nav>
  );
}

export default Navbar;
